import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Paper, Typography, Box, Button, Alert,
    Select, MenuItem, FormControl, InputLabel, CircularProgress,
    Grid, Card, CardContent, Divider
} from '@mui/material';
import {
    CloudDownload as CloudDownloadIcon,
    Storage as StorageIcon,
    TableChart as TableChartIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import api from '../services/api';
import { isAdmin } from '../utils/auth';

function Backup() {
    const navigate = useNavigate();
    const [tabelas, setTabelas] = useState([]);
    const [tabelaSelecionada, setTabelaSelecionada] = useState('');
    const [loadingTabelas, setLoadingTabelas] = useState(false);
    const [exportandoTabela, setExportandoTabela] = useState(false);
    const [exportandoBanco, setExportandoBanco] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        // Verifica se é admin
        if (!isAdmin()) {
            navigate('/');
            return;
        }

        loadTabelas();
    }, [navigate]);

    const loadTabelas = async () => {
        setLoadingTabelas(true);
        setError('');
        try {
            const response = await api.get('/backup/tabelas');
            setTabelas(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar tabelas:', error);
            setError('Erro ao carregar lista de tabelas: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoadingTabelas(false);
        }
    };

    const handleExportarTabela = async () => {
        if (!tabelaSelecionada) {
            setError('Selecione uma tabela para exportar');
            return;
        }

        setExportandoTabela(true);
        setError('');
        setSuccess('');

        try {
            // Cria um link temporário para download
            const token = localStorage.getItem('token');
            // api.defaults.baseURL já inclui '/api', então não precisa adicionar novamente
            const baseURL = api.defaults.baseURL || 'http://localhost:5000/api';
            const url = `${baseURL}/backup/exportar-tabela/${tabelaSelecionada}`;
            
            // Usa fetch para baixar o arquivo
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Erro ao exportar tabela' }));
                throw new Error(errorData.error || 'Erro ao exportar tabela');
            }

            // Obtém o nome do arquivo do header Content-Disposition ou usa padrão
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `${tabelaSelecionada}_${new Date().toISOString().split('T')[0]}.xlsx`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Cria blob e faz download
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            setSuccess(`Tabela "${tabelaSelecionada}" exportada com sucesso!`);
        } catch (error) {
            console.error('Erro ao exportar tabela:', error);
            setError('Erro ao exportar tabela: ' + (error.message || 'Erro desconhecido'));
        } finally {
            setExportandoTabela(false);
        }
    };

    const handleExportarBanco = async () => {
        if (!window.confirm('Deseja baixar o banco de dados completo? Este arquivo contém todos os dados do sistema.')) {
            return;
        }

        setExportandoBanco(true);
        setError('');
        setSuccess('');

        try {
            // Cria um link temporário para download
            const token = localStorage.getItem('token');
            // api.defaults.baseURL já inclui '/api', então não precisa adicionar novamente
            const baseURL = api.defaults.baseURL || 'http://localhost:5000/api';
            const url = `${baseURL}/backup/exportar-banco`;
            
            // Usa fetch para baixar o arquivo
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Erro ao exportar banco' }));
                throw new Error(errorData.error || 'Erro ao exportar banco de dados');
            }

            // Obtém o nome do arquivo do header Content-Disposition ou usa padrão
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `database_${new Date().toISOString().split('T')[0]}.sqlite`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Cria blob e faz download
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            setSuccess('Banco de dados exportado com sucesso!');
        } catch (error) {
            console.error('Erro ao exportar banco:', error);
            setError('Erro ao exportar banco de dados: ' + (error.message || 'Erro desconhecido'));
        } finally {
            setExportandoBanco(false);
        }
    };

    return (
        <Container maxWidth="lg">
            {/* Header */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2,
                    mt: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <StorageIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Exportação de Dados
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Exporte tabelas específicas ou o banco de dados completo (Apenas Administradores)
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Card: Exportar Tabela para Excel */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <TableChartIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">
                                    Exportar Tabela para Excel
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Selecione uma tabela do banco de dados para exportar em formato Excel (.xlsx)
                            </Typography>

                            {loadingTabelas ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Carregando tabelas...</Typography>
                                </Box>
                            ) : (
                                <>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>Tabela</InputLabel>
                                        <Select
                                            value={tabelaSelecionada}
                                            onChange={(e) => setTabelaSelecionada(e.target.value)}
                                            label="Tabela"
                                        >
                                            {tabelas.map((tabela) => (
                                                <MenuItem key={tabela} value={tabela}>
                                                    {tabela}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={exportandoTabela ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
                                        onClick={handleExportarTabela}
                                        disabled={!tabelaSelecionada || exportandoTabela}
                                    >
                                        {exportandoTabela ? 'Exportando...' : 'Baixar Tabela (Excel)'}
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Card: Exportar Banco Completo */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6">
                                    Exportar Banco de Dados Completo
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Baixe o arquivo SQLite completo do banco de dados. Este é um snapshot completo do sistema no momento atual.
                            </Typography>

                            <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SecurityIcon fontSize="small" />
                                    <strong>Atenção:</strong> Este arquivo contém todos os dados do sistema. Mantenha-o seguro e siga as políticas de segurança da sua organização.
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                fullWidth
                                color="primary"
                                startIcon={exportandoBanco ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
                                onClick={handleExportarBanco}
                                disabled={exportandoBanco}
                            >
                                {exportandoBanco ? 'Gerando Backup...' : 'Baixar Banco de Dados (SQLite)'}
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Informações Adicionais */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Informações Importantes
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box component="ul" sx={{ pl: 2 }}>
                    <li>
                        <Typography variant="body2" paragraph>
                            <strong>Exportação de Tabela:</strong> Gera um arquivo Excel (.xlsx) com todos os registros da tabela selecionada.
                        </Typography>
                    </li>
                    <li>
                        <Typography variant="body2" paragraph>
                            <strong>Exportação do Banco:</strong> Usa o comando VACUUM INTO do SQLite para criar um backup seguro e consistente, mesmo com o banco em uso.
                        </Typography>
                    </li>
                    <li>
                        <Typography variant="body2" paragraph>
                            <strong>Segurança:</strong> Todas as exportações são registradas na auditoria do sistema, incluindo quem exportou, quando e qual tabela/banco foi exportado.
                        </Typography>
                    </li>
                    <li>
                        <Typography variant="body2">
                            <strong>Acesso:</strong> Esta funcionalidade está disponível apenas para administradores do sistema.
                        </Typography>
                    </li>
                </Box>
            </Paper>
        </Container>
    );
}

export default Backup;
